import React, { useState } from 'react';

// Healife (com.dcp.humanos) hesabi cihaz tabanlidir: kayit ekrani yoktur, hesap
// ilk aciliksta uretilip telefonun guvenli deposunda tutulan bir UUID'dir.
// Dolayisiyla web'den dogrulamanin tek yolu bu kimlik: uygulama onu Profil >
// Hesap > Cihaz Kimligi altinda gosterir, bu sayfa da onunla gercek API'ye
// login olup DELETE /api/user/me cagirir.
const API_BASE = 'https://dcp-humanos-410d767c97ce.herokuapp.com/api';

const DEVICE_ID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const CONFIRM_WORD = 'DELETE';

const GENERIC_ERROR =
    'Something went wrong on our side. Please try again in a few minutes, or e-mail samedakgul99@gmail.com.';

const DELETED_DATA = [
    'Profile: display name, e-mail, avatar and any linked Google / Apple identifier',
    'Onboarding answers: age, gender, height, weight, goals, habits and experience level',
    'Body measurements, body-fat history and progress photos',
    'Workout sessions, exercise logs, custom and active training programs',
    'Nutrition plans, food log, daily calorie / macro targets and daily checks',
    'Sleep logs and sleep plan',
    'AI coach conversations and chat images',
    'Support tickets and their attachments',
    'Push notification tokens and delivered notifications',
];

const RETAINED_DATA = [
    'Purchase and subscription receipts are held by Google Play / the App Store, not by us. Deleting your account does not cancel an active subscription — cancel it from the store first.',
    'Anonymous, non-identifying server logs may persist for up to 30 days for security and abuse prevention.',
];

class ApiError extends Error {
    constructor(message, status) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

// Backend zarfi her zaman { error, message, code, results } seklinde.
async function apiRequest(path, { method = 'GET', body, token } = {}) {
    let response;
    try {
        response = await fetch(`${API_BASE}${path}`, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': 'en',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            ...(body ? { body: JSON.stringify(body) } : {}),
        });
    } catch {
        throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
    }

    let payload;
    try {
        payload = await response.json();
    } catch {
        throw new ApiError('The server returned an unexpected response.', response.status);
    }

    if (payload.error === true || !response.ok) {
        throw new ApiError(payload.message || 'The request failed.', response.status);
    }

    return payload.results;
}

const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Section = ({ title, children }) => (
    <section className="mb-8">
        <h2 className="text-lg font-custom1 text-dcp-text mb-3">{title}</h2>
        {children}
    </section>
);

const Bullets = ({ items, marker }) => (
    <ul className="space-y-2">
        {items.map((item) => (
            <li key={item} className="flex gap-3 text-sm font-custom2 text-dcp-text-2 leading-relaxed">
                <span className="text-dcp-primary shrink-0">{marker}</span>
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const AccountDeletionPageHealife = () => {
    const [step, setStep] = useState('form'); // form -> confirm -> done
    const [deviceId, setDeviceId] = useState('');
    const [token, setToken] = useState(null);
    const [account, setAccount] = useState(null);
    const [confirmText, setConfirmText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const trimmedId = deviceId.trim();
    const isIdValid = DEVICE_ID_PATTERN.test(trimmedId);

    const handleLookup = async (event) => {
        event.preventDefault();
        if (!isIdValid || isLoading) return;

        setIsLoading(true);
        setError('');
        try {
            const results = await apiRequest('/user/login', {
                method: 'POST',
                body: { deviceId: trimmedId },
            });
            setToken(results.token);
            setAccount(results.user);
            setStep('confirm');
        } catch (err) {
            if (err.status === 404) {
                setError(
                    'No account matches this Device ID. Make sure you copied it from Healife > Profile > Device ID.'
                );
            } else if (err.status === 429) {
                setError('Too many attempts. Please wait a few minutes and try again.');
            } else if (err.status === 403) {
                setError('This account is suspended. Contact support to have it deleted.');
            } else {
                // Backend hata metinleri yer yer Turkce; sayfa Ingilizce oldugu
                // icin ham mesaji sadece yukaridaki bilinen durumlarda gosteriyoruz.
                setError(err.status === 0 ? err.message : GENERIC_ERROR);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (confirmText.trim().toUpperCase() !== CONFIRM_WORD || isLoading) return;

        setIsLoading(true);
        setError('');
        try {
            await apiRequest('/user/me', { method: 'DELETE', token });
            setToken(null);
            setAccount(null);
            setDeviceId('');
            setConfirmText('');
            setStep('done');
        } catch (err) {
            if (err.status === 401) {
                setError('Your session expired before the deletion ran. Please enter your Device ID again.');
                setToken(null);
                setAccount(null);
                setStep('form');
            } else {
                setError(err.status === 0 ? err.message : GENERIC_ERROR);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setToken(null);
        setAccount(null);
        setConfirmText('');
        setError('');
        setStep('form');
    };

    const createdAt = formatDate(account?.createdAt);

    return (
        <div className="min-h-screen bg-dcp-bg pt-24 pb-20 px-6">
            <div className="max-w-2xl mx-auto">
                <header className="mb-10">
                    <p className="text-xs font-custom2 tracking-[0.2em] uppercase text-dcp-primary mb-3">
                        Healife &middot; com.dcp.humanos
                    </p>
                    <h1 className="text-3xl md:text-4xl font-custom1 text-dcp-text mb-4">
                        Delete your account and data
                    </h1>
                    <p className="text-sm font-custom2 text-dcp-text-2 leading-relaxed">
                        Healife has no sign-up screen. Your account is created automatically on first
                        launch and identified by a <span className="text-dcp-text">Device ID</span> stored
                        on your phone. Enter that ID below to permanently erase your account and every
                        piece of data linked to it. The deletion runs immediately — there is nothing to
                        wait for and nothing to undo.
                    </p>
                </header>

                {step === 'form' && (
                    <form
                        onSubmit={handleLookup}
                        className="bg-dcp-surface border border-dcp-border rounded-2xl p-6 md:p-8 mb-10"
                    >
                        <label
                            htmlFor="deviceId"
                            className="block text-sm font-custom2 font-semibold text-dcp-text mb-2"
                        >
                            Device ID
                        </label>
                        <p className="text-xs font-custom2 text-dcp-text-3 mb-4">
                            Open Healife &rarr; Profile &rarr; Device ID &rarr; tap to copy.
                        </p>
                        <input
                            id="deviceId"
                            type="text"
                            autoComplete="off"
                            spellCheck="false"
                            value={deviceId}
                            onChange={(e) => {
                                setDeviceId(e.target.value);
                                setError('');
                            }}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            disabled={isLoading}
                            className="w-full py-3 px-4 bg-dcp-bg border border-dcp-border-dark rounded-xl text-dcp-text font-custom2 text-sm tracking-wide placeholder:text-dcp-text-3 focus:outline-none focus:border-dcp-primary transition-colors disabled:opacity-60"
                        />
                        {trimmedId.length > 0 && !isIdValid && (
                            <p className="mt-2 text-xs font-custom2 text-dcp-text-3">
                                A Device ID looks like 36 characters in 5 dash-separated groups.
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={!isIdValid || isLoading}
                            className={`mt-5 w-full py-3 px-6 rounded-xl font-custom2 font-semibold text-sm transition-colors ${!isIdValid || isLoading
                                ? 'bg-dcp-surface-variant text-dcp-text-3 cursor-not-allowed'
                                : 'bg-dcp-primary text-dcp-on-primary hover:bg-dcp-primary-dark'
                                }`}
                        >
                            {isLoading ? 'Checking…' : 'Find my account'}
                        </button>

                        {error && (
                            <p className="mt-4 text-sm font-custom2 text-dcp-error leading-relaxed">{error}</p>
                        )}
                    </form>
                )}

                {step === 'confirm' && (
                    <div className="bg-dcp-surface border border-dcp-error rounded-2xl p-6 md:p-8 mb-10">
                        <h2 className="text-lg font-custom1 text-dcp-text mb-4">Confirm deletion</h2>

                        <dl className="bg-dcp-bg border border-dcp-border rounded-xl p-4 mb-5 space-y-2">
                            {account?.displayName && (
                                <div className="flex justify-between gap-4 text-sm font-custom2">
                                    <dt className="text-dcp-text-3">Name</dt>
                                    <dd className="text-dcp-text text-right">{account.displayName}</dd>
                                </div>
                            )}
                            {account?.email && (
                                <div className="flex justify-between gap-4 text-sm font-custom2">
                                    <dt className="text-dcp-text-3">E-mail</dt>
                                    <dd className="text-dcp-text text-right break-all">{account.email}</dd>
                                </div>
                            )}
                            {createdAt && (
                                <div className="flex justify-between gap-4 text-sm font-custom2">
                                    <dt className="text-dcp-text-3">Member since</dt>
                                    <dd className="text-dcp-text text-right">{createdAt}</dd>
                                </div>
                            )}
                            {account?.platform && (
                                <div className="flex justify-between gap-4 text-sm font-custom2">
                                    <dt className="text-dcp-text-3">Platform</dt>
                                    <dd className="text-dcp-text text-right capitalize">{account.platform}</dd>
                                </div>
                            )}
                        </dl>

                        <p className="text-sm font-custom2 text-dcp-text-2 leading-relaxed mb-5">
                            This erases the account above together with every record listed below. It cannot
                            be reversed, and a future install of Healife will start from an empty account.
                        </p>

                        <label
                            htmlFor="confirmText"
                            className="block text-sm font-custom2 font-semibold text-dcp-text mb-2"
                        >
                            Type <span className="text-dcp-error">{CONFIRM_WORD}</span> to confirm
                        </label>
                        <input
                            id="confirmText"
                            type="text"
                            autoComplete="off"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={CONFIRM_WORD}
                            disabled={isLoading}
                            className="w-full py-3 px-4 bg-dcp-bg border border-dcp-border-dark rounded-xl text-dcp-text font-custom2 text-sm placeholder:text-dcp-text-3 focus:outline-none focus:border-dcp-error transition-colors disabled:opacity-60"
                        />

                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={confirmText.trim().toUpperCase() !== CONFIRM_WORD || isLoading}
                            className={`mt-5 w-full py-3 px-6 rounded-xl font-custom2 font-semibold text-sm transition-colors ${confirmText.trim().toUpperCase() !== CONFIRM_WORD || isLoading
                                ? 'bg-dcp-surface-variant text-dcp-text-3 cursor-not-allowed'
                                : 'bg-dcp-error text-white hover:opacity-90'
                                }`}
                        >
                            {isLoading ? 'Deleting…' : 'Permanently delete my account'}
                        </button>

                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isLoading}
                            className="mt-3 w-full py-3 px-6 rounded-xl font-custom2 font-semibold text-sm text-dcp-text-2 border border-dcp-border hover:text-dcp-text transition-colors disabled:opacity-60"
                        >
                            Cancel
                        </button>

                        {error && (
                            <p className="mt-4 text-sm font-custom2 text-dcp-error leading-relaxed">{error}</p>
                        )}
                    </div>
                )}

                {step === 'done' && (
                    <div className="bg-dcp-surface border border-dcp-primary rounded-2xl p-6 md:p-8 mb-10">
                        <h2 className="text-lg font-custom1 text-dcp-primary mb-3">Account deleted</h2>
                        <p className="text-sm font-custom2 text-dcp-text-2 leading-relaxed">
                            Your Healife account and all data listed below have been permanently removed from
                            our servers. If Healife is still installed, open it once and it will start as a
                            brand-new, empty account. If you pay for a subscription, remember to cancel it in
                            Google Play or the App Store — stores handle billing, and we cannot cancel it for
                            you.
                        </p>
                    </div>
                )}

                <Section title="What is deleted">
                    <Bullets items={DELETED_DATA} marker="&minus;" />
                </Section>

                <Section title="What is not deleted">
                    <Bullets items={RETAINED_DATA} marker="&bull;" />
                </Section>

                <Section title="Prefer to do it inside the app?">
                    <p className="text-sm font-custom2 text-dcp-text-2 leading-relaxed">
                        Healife offers the same one-tap deletion at{' '}
                        <span className="text-dcp-text">Profile &rarr; Delete Account</span>. It runs exactly
                        the same operation as this page.
                    </p>
                </Section>

                <Section title="Can't find your Device ID?">
                    <p className="text-sm font-custom2 text-dcp-text-2 leading-relaxed">
                        If you have uninstalled the app or cannot reach the Profile screen, e-mail{' '}
                        <a
                            href="mailto:samedakgul99@gmail.com?subject=Healife%20account%20deletion%20request"
                            className="text-dcp-primary underline underline-offset-4"
                        >
                            samedakgul99@gmail.com
                        </a>{' '}
                        from the address linked to your account (or describe your account so we can locate
                        it). Requests are processed within 30 days.
                    </p>
                </Section>
            </div>
        </div>
    );
};

export default AccountDeletionPageHealife;
